/**
 * Gear — everything that makes a body read as a soldier.
 *
 * Plate carrier / chest rig, magazine and admin pouches, radio + antenna, belt,
 * dump pouch, drop-leg holster, knee and elbow pads, helmet with rail, NVG mount and
 * cover, goggles, headset with boom mic, balaclava, shemagh, boonie, beanie — plus a
 * carbine rigid-skinned to the right hand so the character is never empty-handed and
 * costs no extra draw call.
 *
 * Straps are lofted ribbons that follow the actual torso surface, so nothing floats:
 * `vestZ(y, x)` is the shared surface function the vest, the MOLLE rows and the
 * shoulder straps all sit on.
 *
 * owner: characters
 */
import * as THREE from 'three';
import { SLOT, lerp, clamp, smooth } from './Build.js';
import { makeWeightFn, tubeRings, resample } from './Body.js';
import { HEAD, SHELL, skullPoint, helmetBrimY, shellPoint, shellPhiFor } from './Head.js';
import { weaponAnchors } from './Rig.js';

const V = (x, y, z) => new THREE.Vector3(x, y, z);
const _sp = new THREE.Vector3();
const _sk = new THREE.Vector3();
const SHELL_TOP = SHELL.cy + SHELL.ry;

/* ---- shared torso surface -------------------------------------------- */
function pw(rows) {
  return (y) => {
    if (y <= rows[0][0]) return rows[0];
    if (y >= rows[rows.length - 1][0]) return rows[rows.length - 1];
    let i = 0;
    while (i < rows.length - 2 && rows[i + 1][0] < y) i++;
    const a = rows[i], b = rows[i + 1];
    const t = (y - a[0]) / (b[0] - a[0]);
    const o = [y];
    for (let k = 1; k < a.length; k++) o.push(lerp(a[k], b[k], t));
    return o;
  };
}

// y -> [y, half-width, half-depth] of the VEST outer surface (body + ~22 mm of kit)
const vestP = pw([
  [1.060, 0.162, 0.130],
  [1.120, 0.166, 0.134],
  [1.190, 0.176, 0.142],
  [1.260, 0.190, 0.148],
  [1.320, 0.197, 0.150],
  [1.375, 0.192, 0.142],
  [1.420, 0.172, 0.124],
]);

/** Point on the vest front surface at height y and lateral position x. */
function vestZ(y, x) {
  const p = vestP(y);
  const f = clamp(1 - (x / p[1]) ** 2, 0, 1);
  return p[2] * Math.pow(f, 0.32);
}

export function buildGearInto(B, rig, L) {
  const S = rig.scale;
  const I = (n) => rig.index.get(n);

  /** spine-chain weights for a gear vertex at height y (bind space, already scaled) */
  const torsoW = makeWeightFn(rig, [
    [0.90 * S, { hips: 1 }],
    [1.02 * S, { hips: 0.85, spine01: 0.15 }],
    [1.09 * S, { hips: 0.25, spine01: 0.75 }],
    [1.16 * S, { spine01: 0.90, spine02: 0.10 }],
    [1.23 * S, { spine01: 0.30, spine02: 0.70 }],
    [1.29 * S, { spine02: 0.55, chest: 0.45 }],
    [1.36 * S, { spine02: 0.10, chest: 0.90 }],
    [1.46 * S, { chest: 0.85, neck: 0.15 }],
  ]);
  const wY = (y) => torsoW(y);

  const out = {};

  /* ================================================================ *
   * PLATE CARRIER / CHEST RIG
   * ================================================================ */
  {
    const plate = L.vestClass === 'plate';
    const yTop = plate ? 1.400 : 1.385;
    const yBot = plate ? 1.105 : 1.175;
    const ys = [];
    const n = 9;
    for (let i = 0; i < n; i++) ys.push(lerp(yBot, yTop, i / (n - 1)));
    const pts = ys.map((y) => V(0, y * S, 0));
    const rings = tubeRings(pts, (d, t) => {
      const y = lerp(yBot, yTop, t);
      const p = vestP(y);
      const shrink = plate ? 1 : 0.985;
      const taper = t > 0.86 ? lerp(1, 0.90, (t - 0.86) / 0.14) : 1;
      return {
        rx: p[1] * shrink * taper * S, ry: p[2] * shrink * taper * S,
        round: 0.60, dz: (plate ? 0.004 : 0.006) * S,
      };
    }, (d) => wY(yBot * S + d));
    B.setSlot(SLOT.WEBBING).setTint(L.vest);
    B.loft(rings, { sides: 18, capStart: true, capEnd: true });

    // hard front/back armour plates sitting proud of the carrier
    B.setSlot(SLOT.HARD).setTint(0x6f6d62);
    for (const sgn of [1, -1]) {
      B.superBox({
        centre: V(0, 1.268 * S, sgn * (vestZ(1.268, 0) + 0.014) * S),
        half: [0.128 * S, 0.128 * S, 0.020 * S], round: 0.30, segU: 12, segV: 7,
        w: wY(1.268 * S),
      });
    }

    // shoulder straps — routed over the trapezius, not floating above it
    B.setSlot(SLOT.WEBBING).setTint(L.vest);
    for (const s of [-1, 1]) {
      const path = [
        V(s * 0.072, 1.352, vestZ(1.352, s * 0.072) + 0.012),
        V(s * 0.092, 1.402, 0.085),
        V(s * 0.100, 1.436, 0.010),
        V(s * 0.096, 1.424, -0.070),
        V(s * 0.078, 1.360, -0.118),
      ].map((p) => p.multiplyScalar(S));
      const c = V(s * 0.055 * S, 1.330 * S, 0);
      B.strap(path, {
        width: 0.078 * S, thick: 0.024 * S, sides: 8,
        up: (p) => p.clone().sub(c).normalize(),
        wAt: (p) => wY(p.y),
      });
    }

    // cummerbund
    {
      const y = (plate ? 1.150 : 1.205);
      const p = vestP(y);
      const pts2 = [V(0, (y - 0.030) * S, 0), V(0, y * S, 0), V(0, (y + 0.030) * S, 0)];
      B.setTint(0x9c9a8a);
      B.loft(tubeRings(pts2, () => ({ rx: (p[1] + 0.006) * S, ry: (p[2] + 0.005) * S, round: 0.62 }),
        () => wY(y * S)), { sides: 18, capStart: false, capEnd: false });
    }

    // Two-point sling: over the right shoulder, across the back, round to the left hip.
    // It is skinned to the torso, not to the weapon, so it never stretches — but it is
    // the diagonal that stops the chest reading as a plain slab.
    B.setSlot(SLOT.WEBBING).setTint(0x35342c);
    B.strap([
      V(0.104, 1.238, vestZ(1.238, 0.104) + 0.030),
      V(0.126, 1.320, 0.088),
      V(0.116, 1.404, 0.020),
      V(0.086, 1.398, -0.076),
      V(-0.012, 1.318, -0.148),
      V(-0.118, 1.196, -0.108),
      V(-0.152, 1.096, -0.010),
    ].map((p) => p.multiplyScalar(S)), {
      width: 0.030 * S, thick: 0.010 * S, sides: 6,
      up: (p) => V(p.x, 0.06 * S, p.z).normalize(), wAt: (p) => wY(p.y),
    });
    B.setSlot(SLOT.HARD).setTint(0x54524a);
    B.superBox({
      centre: V(0.118 * S, 1.292 * S, 0.104 * S), half: [0.014 * S, 0.020 * S, 0.010 * S],
      round: 0.3, segU: 7, segV: 4, w: wY(1.292 * S),
    });
    B.setSlot(SLOT.WEBBING);

    // MOLLE rows across the front plate
    B.setTint(0x8d8b7c);
    for (let r = 0; r < 3; r++) {
      const y = 1.235 + r * 0.048;
      const path = [];
      for (let i = 0; i <= 6; i++) {
        const x = lerp(-0.108, 0.108, i / 6);
        path.push(V(x * S, y * S, (vestZ(y, x) + 0.016) * S));
      }
      B.strap(path, {
        width: 0.020 * S, thick: 0.008 * S, sides: 6,
        up: () => V(0, 0, 1), wAt: (p) => wY(p.y),
      });
    }
  }

  /* ================================================================ *
   * POUCHES
   * ================================================================ */
  {
    B.setSlot(SLOT.WEBBING).setTint(L.vest);
    const nMag = L.magPouches;
    const yRow = L.vestClass === 'plate' ? 1.178 : 1.235;
    const perRow = Math.min(4, nMag);
    for (let i = 0; i < nMag; i++) {
      const row = Math.floor(i / 4);
      const k = i % 4;
      const cnt = Math.min(perRow, nMag - row * 4);
      const x = cnt === 1 ? 0 : lerp(-0.098, 0.098, k / Math.max(1, cnt - 1));
      const y = yRow + row * 0.098;
      const z = vestZ(y, x) + 0.036;
      const w = wY(y * S);
      B.setTint(L.vest);
      B.superBox({
        centre: V(x * S, y * S, z * S),
        quat: new THREE.Quaternion().setFromEuler(new THREE.Euler(0, -x * 1.6, 0)),
        half: [0.042 * S, 0.070 * S, 0.036 * S], round: 0.24, segU: 10, segV: 6, w,
      });
      // flap + pull tab — a value step, not just a shape, so the pouch reads at range
      B.setTint(0x6e6c5e);
      B.superBox({
        centre: V(x * S, (y + 0.064) * S, (z + 0.006) * S),
        half: [0.044 * S, 0.019 * S, 0.038 * S], round: 0.28, segU: 9, segV: 4, w,
      });
      B.setTint(0x2f2e28);
      B.superBox({
        centre: V(x * S, (y + 0.044) * S, (z + 0.036) * S),
        half: [0.008 * S, 0.020 * S, 0.006 * S], round: 0.4, segU: 6, segV: 4, w,
      });
    }

    if (L.adminPouch) {
      const y = 1.318, x = -0.052;
      const w = wY(y * S);
      B.setTint(0x8b8979);
      B.superBox({
        centre: V(x * S, y * S, (vestZ(y, x) + 0.030) * S),
        quat: new THREE.Quaternion().setFromEuler(new THREE.Euler(0, -x * 1.6, 0)),
        half: [0.062 * S, 0.046 * S, 0.024 * S], round: 0.28, segU: 10, segV: 6, w,
      });
    }

    if (L.grenadePouch) {
      for (const s of [-1, 1]) {
        const y = 1.300, x = s * 0.124;
        const w = wY(y * S);
        B.setTint(L.vest);
        B.superBox({
          centre: V(x * S, y * S, (vestZ(y, x) * 0.72 + 0.020) * S),
          quat: new THREE.Quaternion().setFromEuler(new THREE.Euler(0, -s * 0.9, 0)),
          half: [0.030 * S, 0.048 * S, 0.030 * S], round: 0.34, segU: 9, segV: 6, w,
        });
      }
    }

    if (L.radio) {
      const y = 1.290, x = L.seed % 2 ? 0.098 : -0.098;
      const w = wY(y * S);
      B.setSlot(SLOT.HARD).setTint(0x3b3d3a);
      B.superBox({
        centre: V(x * S, y * S, -(vestZ(y, x) + 0.026) * S),
        half: [0.046 * S, 0.070 * S, 0.028 * S], round: 0.22, segU: 10, segV: 6, w,
      });
      out.radioTop = V(x * S, (y + 0.075) * S, -(vestZ(y, x) + 0.024) * S);
    }

    if (L.dumpPouch) {
      const y = 1.030, x = -0.132;
      const w = wY(y * S);
      B.setSlot(SLOT.WEBBING).setTint(0x7c7a6c);
      B.superBox({
        centre: V(x * S, y * S, 0.055 * S),
        half: [0.040 * S, 0.062 * S, 0.044 * S], round: 0.40, segU: 10, segV: 6,
        w: [[I('beltA'), 0.55], ...w.map(([b, ww]) => [b, ww * 0.45])],
      });
    }
  }

  /* ================================================================ *
   * BELT + HOLSTER
   * ================================================================ */
  {
    const y = 0.965;
    const pts = [V(0, (y - 0.026) * S, 0), V(0, y * S, 0), V(0, (y + 0.026) * S, 0)];
    B.setSlot(SLOT.WEBBING).setTint(0x6d6b5e);
    B.loft(tubeRings(pts, () => ({ rx: 0.168 * S, ry: 0.128 * S, round: 0.66 }),
      () => wY(y * S)), { sides: 16, capStart: false, capEnd: false });
    B.setSlot(SLOT.HARD).setTint(0x8f8d80);
    B.superBox({
      centre: V(0, y * S, 0.130 * S), half: [0.030 * S, 0.022 * S, 0.016 * S],
      round: 0.30, segU: 8, segV: 4, w: wY(y * S),
    });

    if (L.holster) {
      const wH = [[I('holsterA'), 0.62], [I('thighR'), 0.38]];
      B.setSlot(SLOT.WEBBING).setTint(0x4b4a41);
      B.superBox({
        centre: V(0.158 * S, 0.712 * S, 0.030 * S),
        quat: new THREE.Quaternion().setFromEuler(new THREE.Euler(0.06, 0, -0.06)),
        half: [0.036 * S, 0.088 * S, 0.048 * S], round: 0.30, segU: 10, segV: 6, w: wH,
      });
      B.setSlot(SLOT.HARD).setTint(0x2f2e2a);
      B.superBox({
        centre: V(0.158 * S, 0.800 * S, 0.036 * S),
        half: [0.020 * S, 0.038 * S, 0.030 * S], round: 0.30, segU: 8, segV: 5, w: wH,
      });
      // suspension strap up to the belt + two thigh straps
      B.setSlot(SLOT.WEBBING).setTint(0x5f5e52);
      B.strap([
        V(0.150 * S, 0.955 * S, 0.052 * S),
        V(0.156 * S, 0.870 * S, 0.048 * S),
        V(0.158 * S, 0.795 * S, 0.044 * S),
      ], {
        width: 0.026 * S, thick: 0.008 * S, sides: 6, up: () => V(0, 0, 1),
        wAt: (p) => (p.y > 0.90 * S ? wY(p.y) : [[I('thighR'), 0.7], [I('hips'), 0.3]]),
      });
      for (let k = 0; k < 2; k++) {
        const y2 = (0.700 - k * 0.070) * S;
        const th = rig.bind.thighR;
        const pts2 = [V(th.x, y2 - 0.012 * S, th.z), V(th.x, y2, th.z), V(th.x, y2 + 0.012 * S, th.z)];
        B.loft(tubeRings(pts2, () => ({ rx: 0.090 * S, ry: 0.092 * S, round: 0.86 }),
          () => [[I('thighR'), 1]]), { sides: 10, capStart: false, capEnd: false });
      }
    }
  }

  /* ================================================================ *
   * TROUSERS — cargo pockets and blousing.
   * Legs are the emptiest part of a lofted humanoid; without these the silhouette
   * below the belt is two smooth tubes and the whole character reads as a toy.
   * ================================================================ */
  {
    B.setSlot(SLOT.CLOTH).setTint(L.uniform);
    for (const s of ['L', 'R']) {
      const sg = s === 'L' ? -1 : 1;
      const th = rig.bind['thigh' + s];
      const w = [[I('thigh' + s), 1]];
      // cargo pocket on the outside of the thigh
      B.superBox({
        centre: V(th.x + sg * 0.070 * S, 0.790 * S, 0.010 * S),
        quat: new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, sg * 0.10)),
        half: [0.032 * S, 0.078 * S, 0.062 * S], round: 0.40, segU: 10, segV: 6, w,
      });
      B.setTint(0xb8b5a2);
      B.superBox({
        centre: V(th.x + sg * 0.074 * S, 0.856 * S, 0.010 * S),
        half: [0.030 * S, 0.018 * S, 0.058 * S], round: 0.40, segU: 9, segV: 4, w,
      });
      B.setTint(L.uniform);
      // knee reinforcement panel
      const kn = rig.bind['shin' + s];
      const wk = [[I('shin' + s), 0.55], [I('thigh' + s), 0.45]];
      B.superBox({
        centre: V(kn.x, kn.y + 0.018 * S, kn.z + 0.016 * S),
        half: [0.078 * S, 0.082 * S, 0.080 * S], round: 0.48, segU: 11, segV: 6, w: wk,
      });
      // trouser blousing over the boot top
      const an = rig.bind['foot' + s];
      const wb = [[I('shin' + s), 0.85], [I('foot' + s), 0.15]];
      const bp = resample([
        [an.x, 0.400 * S, an.z], [an.x, 0.362 * S, an.z], [an.x, 0.330 * S, an.z],
      ], 4);
      B.loft(tubeRings(bp, (d, t) => ({
        rx: lerp(0.062, 0.090, t) * S, ry: lerp(0.066, 0.094, t) * S, round: 0.86,
      }), () => wb), { sides: 12, capStart: false, capEnd: true });
    }
  }

  /* ================================================================ *
   * PADS
   * ================================================================ */
  if (L.kneePads) {
    B.setSlot(SLOT.HARD).setTint(L.pad);
    for (const s of ['L', 'R']) {
      const k = rig.bind['shin' + s];
      const w = [[I('shin' + s), 0.66], [I('thigh' + s), 0.34]];
      B.superBox({
        centre: V(k.x, (k.y + 0.012 * S), k.z + 0.062 * S),
        half: [0.056 * S, 0.076 * S, 0.036 * S], round: 0.52, segU: 12, segV: 7, w,
      });
      B.setSlot(SLOT.WEBBING).setTint(0x4a4941);
      for (let i = 0; i < 2; i++) {
        const y2 = k.y + (i === 0 ? 0.062 : -0.052) * S;
        const pts2 = [V(k.x, y2 - 0.010 * S, k.z), V(k.x, y2, k.z), V(k.x, y2 + 0.010 * S, k.z)];
        B.loft(tubeRings(pts2, () => ({ rx: 0.076 * S, ry: 0.080 * S, round: 0.86 }),
          () => w), { sides: 10, capStart: false, capEnd: false });
      }
      B.setSlot(SLOT.HARD).setTint(L.pad);
    }
  }
  if (L.elbowPads) {
    B.setSlot(SLOT.HARD).setTint(L.pad);
    for (const s of ['L', 'R']) {
      const e = rig.bind['forearm' + s];
      const w = [[I('forearm' + s), 0.55], [I('upperArm' + s), 0.45]];
      B.superBox({
        centre: V(e.x + (s === 'L' ? -0.010 : 0.010) * S, e.y + 0.004 * S, e.z - 0.042 * S),
        half: [0.042 * S, 0.056 * S, 0.028 * S], round: 0.54, segU: 10, segV: 6, w,
      });
    }
  }
  if (L.shoulderPads) {
    B.setSlot(SLOT.WEBBING).setTint(L.vest);
    for (const s of ['L', 'R']) {
      const a = rig.bind['upperArm' + s];
      const w = [[I('upperArm' + s), 0.72], [I('chest'), 0.28]];
      B.superBox({
        centre: V(a.x * 1.04, a.y - 0.010 * S, a.z - 0.004 * S),
        half: [0.058 * S, 0.072 * S, 0.070 * S], round: 0.46, segU: 11, segV: 6, w,
      });
    }
  }

  /* ================================================================ *
   * HEAD KIT
   *
   * Everything here is placed by SAMPLING THE SKULL (`skullPoint`) or the helmet shell
   * (`shellPoint`) rather than by typing world coordinates. That is deliberate: the
   * old head kit carried hand-typed heights against a head that has since changed
   * size, and the same class of mistake is what put a helmet on top of a cranium
   * instead of over it, sank a balaclava inside a skull, and drove a headset boom
   * straight through a cheek. A mask offset 6 mm along the skull normal cannot be
   * inside the skull; a shell fitted to the skull cannot leave the cranium bare.
   * ================================================================ */
  const wHead = [[I('head'), 1]];
  const wNeck = [[I('neck'), 0.6], [I('head'), 0.4]];

  /** Point on the head surface at height `y`, angle `th` (0 = face), pushed `off` out. */
  const onSkull = (y, th, off = 0) => skullPoint(y, th, _sp, off).clone().multiplyScalar(S);
  /** Point on the helmet shell at height `y`, angle `th`, `off` outside the shell. */
  const onShell = (y, th, off = 0) => shellPoint(shellPhiFor(y), th, _sp, off).clone().multiplyScalar(S);

  /**
   * A closed shell over the skull between two heights — the shape every soft head
   * covering wants. `inflate(y, th)` returns the offset along the skull normal.
   */
  const skullShell = (y0, y1, rows, sides, inflate, w, capTop) => {
    const grid = [];
    for (let i = 0; i <= rows; i++) {
      const y = lerp(y0, y1, i / rows);
      const row = [];
      for (let j = 0; j <= sides; j++) {
        const th = ((j % sides) / sides) * Math.PI * 2;
        skullPoint(y, th, _sp, inflate(y, th));
        _sp.multiplyScalar(S);
        row.push(B.pushVertex(_sp, (j / sides) * 0.5 * S, y * S, w));
      }
      B._seams.push(row[0], row[sides]);
      grid.push(row);
    }
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < sides; j++) B.quad(grid[i][j], grid[i][j + 1], grid[i + 1][j + 1], grid[i + 1][j]);
    }
    if (capTop) {
      const top = grid[rows];
      const c = B.pushVertex(_sp.set(0, (y1 + 0.006) * S, -0.004 * S), 0, 0, w);
      for (let j = 0; j < sides; j++) B.tri(c, top[j], top[j + 1]);
    }
    return grid;
  };

  if (L.faceCover === 'balaclava') {
    B.setSlot(SLOT.CLOTH).setTint(0x3a352e).setUV(2.4, 0.22, 0.66, 0.5);
    // Lower face: a 5 mm shell over the jaw, mouth and nose, open at the eyes. Because
    // it follows the skull it picks up the nose and the chin instead of blanking them.
    skullShell(HEAD.jawBottomY - 0.004, HEAD.eyeY - 0.007, 7, 22, (y, th) => {
      const front = clamp(Math.cos(th), 0, 1);
      // fades out over the top of the cheekbone so there is no hard floating edge
      return 0.0050 + 0.0020 * front - 0.0030 * smooth(1.6533, HEAD.eyeY - 0.007, y);
    }, wHead, false);
    // the fold across the bridge of the nose
    B.setTint(0x2c2822);
    skullShell(1.6533, 1.6740, 2, 20, () => 0.0072, wHead, false);
    // neck gaiter
    const pts = resample([[0, 1.392 * S, -0.002 * S], [0, 1.460 * S, -0.006 * S], [0, 1.536 * S, -0.010 * S]], 4);
    B.loft(tubeRings(pts, (d, t) => ({ rx: lerp(0.078, 0.050, t) * S, ry: lerp(0.082, 0.055, t) * S, round: 0.86 }),
      () => wNeck), { sides: 12, capStart: false, capEnd: false });
    B.setUV(1);
  }

  if (L.shemagh) {
    B.setSlot(SLOT.CLOTH).setTint(0xd6cdb2).setUV(1.7, 0.44, 0.12, -0.7);
    // wraps the neck and comes UP over the mouth — the recon variant's face read
    const pts = resample([
      [0, 1.372 * S, -0.006 * S], [0, 1.430 * S, -0.002 * S],
      [0, 1.492 * S, 0.006 * S], [0, 1.540 * S, 0.014 * S],
    ], 6);
    B.loft(tubeRings(pts, (d, t) => ({
      rx: lerp(0.118, 0.076, t) * S, ry: lerp(0.122, 0.086, t) * S, round: 0.82,
    }), () => wNeck), { sides: 14, capStart: false, capEnd: false });
    B.setTint(0xc4bb9e);
    skullShell(HEAD.jawBottomY - 0.006, HEAD.noseBaseY + 0.013, 6, 22, (y, th) => {
      const front = clamp(Math.cos(th), 0, 1);
      return 0.0085 + 0.0045 * front - 0.0055 * smooth(1.6273, HEAD.noseBaseY + 0.013, y);
    }, wHead, false);
    // hanging tail, driven by the verlet chain
    const tailW = (t) => (t < 0.5
      ? [[I('shemaghA'), 0.7 + t], [I('neck'), 0.3 - t]]
      : [[I('shemaghB'), (t - 0.5) * 2], [I('shemaghA'), 1 - (t - 0.5) * 2]]);
    const tp = resample([
      [0, 1.438 * S, -0.082 * S], [0, 1.380 * S, -0.108 * S],
      [0, 1.312 * S, -0.126 * S], [0, 1.250 * S, -0.136 * S],
    ], 6);
    B.loft(tubeRings(tp, (d, t) => ({ rx: lerp(0.078, 0.052, t) * S, ry: lerp(0.022, 0.014, t) * S, round: 0.55 }),
      (d) => tailW(clamp(d / (0.21 * S), 0, 1))), { sides: 8, capStart: false, capEnd: true });
    B.setUV(1);
  }

  if (L.helmet) {
    /* ---- the shell ----------------------------------------------------
     * An ellipsoid dome fitted over the skull and clipped at a brim that rides high
     * across the forehead and drops over the ears and the occiput. `helmetBrimY` and
     * `shellPoint` both live in Head.js next to `skullPoint`, so the clearance is a
     * property of the model and not of a number somebody typed. Measured minimum
     * clearance over the whole overlap is 12 mm, which is a shell plus its pad.
     *
     * The old version was a vertical loft with hand-tilted rings: at the front it rode
     * up off the brow and left the top of the cranium bare below it, which is the
     * "helmets perch on top of the skull" in char_squad. */
    const HSIDES = 28, HROWS = 7;
    B.setSlot(SLOT.HARD).setTint(L.helmetCover ? 0x8f8b78 : 0x54544a);
    /**
     * Its own uv pitch — a helmet cover is a fine ripstop, not the 0.5 m weave the
     * uniform runs at, and running both at one scale is what made it read as burlap.
     *
     * The uvs are in METRES OF SHELL SURFACE, like every other part in this module.
     * The first attempt emitted `u = j/28 * 0.62`, `v = i * 0.03` and then scaled the
     * whole thing by 0.34, which mapped a 20 cm helmet onto a 21 x 7 mm patch of the
     * texture. One smooth corner of the map got magnified across the entire rear
     * quadrant, so the back half of the helmet rendered as a different, warmer,
     * untextured material from the front — clearly visible in `char_closeup`.
     */
    B.setUV(1.7, 0.19, 0.73, 0.35);
    {
      const grid = [];
      const phiB = [];
      for (let j = 0; j <= HSIDES; j++) {
        const th = ((j % HSIDES) / HSIDES) * Math.PI * 2;
        phiB.push(shellPhiFor(helmetBrimY(th)));
      }
      const uPer = Math.PI * (SHELL.ax + (SHELL.zf + SHELL.zb) * 0.5) * 0.5;  // quarter perimeter
      for (let i = 0; i <= HROWS; i++) {
        const t = Math.pow(1 - i / HROWS, 1.15);      // dense near the brim
        const row = [];
        for (let j = 0; j <= HSIDES; j++) {
          const th = ((j % HSIDES) / HSIDES) * Math.PI * 2;
          const vArc = (1 - t) * phiB[j] * SHELL.ry;  // metres of arc down from the crown
          shellPoint(phiB[j] * t, th, _sp).multiplyScalar(S);
          row.push(B.pushVertex(_sp, (j / HSIDES) * 4 * uPer * S, vArc * S, wHead));
        }
        B._seams.push(row[0], row[HSIDES]);
        grid.push(row);
      }
      for (let i = 0; i < HROWS; i++) {
        for (let j = 0; j < HSIDES; j++) B.quad(grid[i][j], grid[i][j + 1], grid[i + 1][j + 1], grid[i + 1][j]);
      }
      // crown cap
      const top = grid[HROWS];
      const cc = B.pushVertex(_sp.set(0, (SHELL_TOP + 0.001) * S, 0), uPer * S, 0.16 * S, wHead);
      for (let j = 0; j < HSIDES; j++) B.tri(cc, top[j], top[j + 1]);

      // rim: skirt down past the brim, then fold back inside so the shell has a real
      // edge thickness instead of the zero-width cut that reads as paper
      const skirt = [];
      const innerA = [];
      const innerB = [];
      for (let j = 0; j <= HSIDES; j++) {
        const th = ((j % HSIDES) / HSIDES) * Math.PI * 2;
        shellPoint(phiB[j] + 0.068, th, _sp).multiplyScalar(S);
        skirt.push(B.pushVertex(_sp, (j / HSIDES) * 4 * uPer * S, -0.007 * S, wHead));
      }
      /**
        * The inner surface is a 3 cm LIP, not a cone.
        *
        * The first version folded back to `phiB - 0.34` at 13 mm of shell thickness,
        * which put the inner ring 1.7 mm off the forehead — near-tangent geometry that
        * renders as a light/dark plate cutting straight across the face in
        * `char_closeup`, and lets the brow surface through it. `-0.10` keeps the lip
        * inside the rim where the shell is still 13 mm clear of the skull everywhere.
        */
      B.setTint(0x24241f);
      // Hard floor on the liner: whatever the shell maths says, push it out until it is
      // at least 10 mm clear of the skull at the same height and angle. This is the
      // guarantee, not the arithmetic above it — the liner is the one helmet surface
      // that runs nearly parallel to the forehead, and "nearly parallel and 2 mm apart"
      // is what draws a grey plate across a face.
      const liner = (phi, th, t) => {
        shellPoint(phi, th, _sp, t);
        skullPoint(Math.min(_sp.y, HEAD.crownY), th, _sk);
        const rs = Math.hypot(_sk.x, _sk.z) + 0.010;
        const r = Math.hypot(_sp.x, _sp.z);
        if (r > 1e-6 && r < rs) { _sp.x *= rs / r; _sp.z *= rs / r; }
        return _sp.multiplyScalar(S);
      };
      for (let j = 0; j <= HSIDES; j++) {
        const th = ((j % HSIDES) / HSIDES) * Math.PI * 2;
        liner(phiB[j] + 0.055, th, -0.007);
        innerA.push(B.pushVertex(_sp, (j / HSIDES) * 4 * uPer * S, -0.013 * S, wHead));
        liner(phiB[j] - 0.06, th, -0.007);
        innerB.push(B.pushVertex(_sp, (j / HSIDES) * 4 * uPer * S, 0.012 * S, wHead));
      }
      B._seams.push(skirt[0], skirt[HSIDES], innerA[0], innerA[HSIDES], innerB[0], innerB[HSIDES]);
      for (let j = 0; j < HSIDES; j++) {
        B.quad(grid[0][j + 1], grid[0][j], skirt[j], skirt[j + 1]);
        B.quad(skirt[j + 1], skirt[j], innerA[j], innerA[j + 1]);
        B.quad(innerA[j + 1], innerA[j], innerB[j], innerB[j + 1]);
      }
    }
    B.setUV(1);

    // side rails, following the shell
    B.setSlot(SLOT.HARD).setTint(0x3c3b36);
    for (const s of [-1, 1]) {
      B.strap([
        onShell(1.7020, s * 0.95, 0.006),
        onShell(1.7100, s * 1.5708, 0.006),
        onShell(1.7020, s * 2.30, 0.006),
      ], {
        width: 0.024 * S, thick: 0.011 * S, sides: 6,
        up: (p) => V(p.x, 0.16 * S, p.z * 0.35).normalize(), w: wHead,
      });
    }
    // NVG shroud + folded arm on the brow — the loudest single silhouette cue that
    // this is a modern operator and not a WW2 GI
    if (L.nvgMount) {
      B.setTint(0x33322e);
      const shroud = onShell(1.7540, 0, 0.004);
      B.superBox({
        centre: shroud, half: [0.032 * S, 0.024 * S, 0.020 * S],
        round: 0.26, segU: 9, segV: 5, w: wHead,
      });
      const arm = onShell(1.7800, 0, 0.008);
      B.superBox({
        centre: arm, half: [0.016 * S, 0.021 * S, 0.024 * S],
        round: 0.28, segU: 8, segV: 5, w: wHead,
      });
      B.setTint(0x22221f);
      B.superBox({
        centre: V(0, 1.7965 * S, (arm.z / S - 0.008) * S), half: [0.022 * S, 0.012 * S, 0.018 * S],
        round: 0.30, segU: 8, segV: 4, w: wHead,
      });
    }
    // rail light / IR strobe on the rails
    if (L.railLights) {
      B.setTint(0x6a6a64);
      B.superBox({
        centre: onShell(1.7060, -1.35, 0.014), half: [0.018 * S, 0.016 * S, 0.030 * S],
        round: 0.32, segU: 8, segV: 4, w: wHead,
      });
      B.setTint(0x3a3a36);
      B.superBox({
        centre: onShell(1.7100, 1.90, 0.014), half: [0.016 * S, 0.022 * S, 0.024 * S],
        round: 0.34, segU: 8, segV: 4, w: wHead,
      });
    }
    // velcro battery pack at the back — counterweight, and it breaks the dome silhouette
    B.setSlot(SLOT.WEBBING).setTint(0x53524a);
    B.superBox({
      centre: onShell(1.7300, Math.PI, 0.012), half: [0.046 * S, 0.036 * S, 0.024 * S],
      round: 0.34, segU: 9, segV: 5, w: wHead,
    });
    // chinstrap: down in front of the ear, onto a chin cup under the jaw
    for (const s of [-1, 1]) {
      B.setSlot(SLOT.WEBBING).setTint(0x3b3a34);
      B.strap([
        onShell(1.6920, s * 1.45, 0.002),
        onSkull(1.6480, s * 1.36, 0.008),
        onSkull(1.6000, s * 1.16, 0.008),
        onSkull(1.5660, s * 0.72, 0.008),
        onSkull(HEAD.jawBottomY + 0.001, s * 0.22, 0.008),
      ], {
        width: 0.011 * S, thick: 0.005 * S, sides: 5,
        up: (p) => V(p.x, 0.08 * S, p.z).normalize(), w: wHead,
      });
    }
    B.setTint(0x33322c);
    B.superBox({
      centre: V(0, (HEAD.jawBottomY - 0.007) * S, 0.036 * S),
      half: [0.023 * S, 0.008 * S, 0.019 * S], round: 0.55, segU: 8, segV: 4, w: wHead,
    });
  } else if (L.boonie) {
    B.setSlot(SLOT.CLOTH).setTint(L.uniform).setUV(0.9, 0.51, 0.28, 0.9);
    // crown, fitted to the skull, then a soft down-turned brim
    skullShell(1.6946, HEAD.crownY, 5, 22, (y) => 0.010 + 0.006 * smooth(1.6946, 1.7622, y), wHead, true);
    const bp = resample([
      [0, 1.7057 * S, -0.004 * S], [0, 1.7001 * S, -0.004 * S], [0, 1.6950 * S, -0.004 * S],
    ], 4);
    B.loft(tubeRings(bp, (d, t) => ({
      rx: lerp(0.090, 0.172, t) * S, ry: lerp(0.096, 0.180, t) * S, round: 0.9,
    }), () => wHead), { sides: 20, capStart: false, capEnd: true });
    B.setUV(1);
  } else if (L.beanie) {
    B.setSlot(SLOT.CLOTH).setTint(0x4a4740).setUV(1.4, 0.66, 0.41, -0.4);
    skullShell(1.6677, HEAD.crownY, 6, 22, (y) => 0.006 + 0.008 * smooth(1.6677, 1.7434, y), wHead, true);
    // rolled brim
    const bp = resample([
      [0, 1.6721 * S, -0.004 * S], [0, 1.6796 * S, -0.004 * S], [0, 1.6871 * S, -0.004 * S],
    ], 4);
    B.loft(tubeRings(bp, () => ({ rx: 0.092 * S, ry: 0.104 * S, round: 0.88 }),
      () => wHead), { sides: 16, capStart: false, capEnd: false });
    B.setUV(1);
  }

  if (L.goggles !== 'none') {
    const onHelmet = L.goggles === 'helmet' && L.helmet;
    // Worn on the helmet the goggles are PUSHED UP, clear of the brow. At 1.752 they
    // sat across the eye line as soon as the look-at layer pitched the head down, and
    // a soldier with no eyes is a mannequin again.
    const y = onHelmet ? 1.7810 : HEAD.eyeY + 0.004;
    B.setSlot(SLOT.WEBBING).setTint(0x46453d);
    // strap: a band round whichever surface it is actually worn on
    {
      const n = 20, rowA = [], rowB = [];
      for (let j = 0; j <= n; j++) {
        const th = ((j % n) / n) * Math.PI * 2;
        const a = onHelmet ? onShell(y - 0.011, th, 0.004) : onSkull(y - 0.011, th, 0.007);
        const b = onHelmet ? onShell(y + 0.011, th, 0.004) : onSkull(y + 0.011, th, 0.007);
        rowA.push(B.pushVertex(a, (j / n) * 0.6 * S, 0, wHead));
        rowB.push(B.pushVertex(b, (j / n) * 0.6 * S, 0.022 * S, wHead));
      }
      B._seams.push(rowA[0], rowA[n], rowB[0], rowB[n]);
      for (let j = 0; j < n; j++) B.quad(rowA[j], rowA[j + 1], rowB[j + 1], rowB[j]);
    }
    // frame + lens, sitting on the front of that band
    const front = onHelmet ? onShell(y, 0, 0.012) : onSkull(y, 0, 0.016);
    B.setSlot(SLOT.HARD).setTint(0x24231f);
    B.superBox({
      centre: V(0, front.y, front.z - 0.005 * S), half: [0.052 * S, 0.018 * S, 0.019 * S],
      round: 0.34, segU: 12, segV: 6, w: wHead,
    });
    B.setTint(0x36402f);
    B.superBox({
      centre: V(0, front.y + 0.002 * S, front.z + 0.010 * S), half: [0.042 * S, 0.012 * S, 0.012 * S],
      round: 0.38, segU: 11, segV: 5, w: wHead,
    });
  }

  if (L.headset) {
    // earcups sit over the ear and outboard of the shell — under the helmet brim,
    // which is where a real comms headset goes
    const ey = (HEAD.earTopY + HEAD.earBotY) * 0.5;
    B.setSlot(SLOT.HARD).setTint(0x2b2a27);
    for (const s of [-1, 1]) {
      // Set BACK off the ear: a low-profile comms cup covers the canal, not the whole
      // pinna, and leaving the helix visible is what makes the ear read at all.
      B.superBox({
        centre: V(s * 0.0930 * S, (ey - 0.004) * S, -0.024 * S),
        half: [0.021 * S, 0.032 * S, 0.030 * S], round: 0.45, segU: 10, segV: 6, w: wHead,
      });
      B.setTint(0x4d4c46);
      B.superBox({
        centre: V(s * 0.1105 * S, (ey - 0.004) * S, -0.024 * S),
        half: [0.008 * S, 0.024 * S, 0.023 * S], round: 0.48, segU: 8, segV: 5, w: wHead,
      });
      B.setTint(0x2b2a27);
    }
    // band round the BACK of the skull, under the helmet's rear brim
    B.strap([
      V(-0.0900 * S, (ey + 0.018) * S, -0.032 * S),
      V(-0.0620 * S, 1.6860 * S, -0.078 * S),
      V(0, 1.6900 * S, -0.098 * S),
      V(0.0620 * S, 1.6860 * S, -0.078 * S),
      V(0.0900 * S, (ey + 0.018) * S, -0.032 * S),
    ], { width: 0.016 * S, thick: 0.009 * S, sides: 5, up: (p) => V(p.x, 0.2 * S, p.z).normalize(), w: wHead });
    /**
     * Boom mic. The old one was a straight stick from the earcup to a point 5 cm in
     * front of the mouth, which put its middle third INSIDE the cheek — visible as a
     * black line crossing the face in char_closeup. This one is routed through points
     * measured off the skull surface and pushed 14 mm clear of it, so it arcs round
     * the jaw the way a real boom does.
     */
    B.strap([
      V(-0.0880 * S, (ey - 0.018) * S, 0.004 * S),
      onSkull(1.6220, -1.28, 0.016),
      onSkull(1.6080, -1.00, 0.020),
      onSkull(1.6055, -0.62, 0.026),
    ], { width: 0.010 * S, thick: 0.008 * S, sides: 5, up: () => V(0, 1, 0), w: wHead });
    const tip = onSkull(1.6050, -0.50, 0.030);
    B.superBox({
      centre: tip, half: [0.012 * S, 0.010 * S, 0.010 * S],
      round: 0.6, segU: 7, segV: 4, w: wHead,
    });
  }

  /* ================================================================ *
   * ANTENNA — verlet-driven
   * ================================================================ */
  if (L.antenna) {
    const base = out.radioTop || V(0.098 * S, 1.360 * S, -0.150 * S);
    const tip = V(base.x + 0.030 * S, base.y + 0.430 * S, base.z - 0.056 * S);
    const pts = resample([
      [base.x, base.y, base.z],
      [lerp(base.x, tip.x, 0.34), lerp(base.y, tip.y, 0.34), lerp(base.z, tip.z, 0.34)],
      [lerp(base.x, tip.x, 0.67), lerp(base.y, tip.y, 0.67), lerp(base.z, tip.z, 0.67)],
      [tip.x, tip.y, tip.z],
    ], 7);
    const aw = (t) => {
      if (t < 0.34) return [[I('chest'), 1 - t / 0.34], [I('antennaA'), t / 0.34]];
      if (t < 0.67) return [[I('antennaA'), 1 - (t - 0.34) / 0.33], [I('antennaB'), (t - 0.34) / 0.33]];
      return [[I('antennaB'), 1 - (t - 0.67) / 0.33], [I('antennaC'), (t - 0.67) / 0.33]];
    };
    const total = base.distanceTo(tip);
    B.setSlot(SLOT.HARD).setTint(0x1e1e1c);
    B.loft(tubeRings(pts, (d) => ({
      rx: lerp(0.0075, 0.0028, d / total) * S, ry: lerp(0.0075, 0.0028, d / total) * S, round: 1,
    }), (d) => aw(clamp(d / total, 0, 1))), { sides: 5, capStart: true, capEnd: true });
  }

  /* ================================================================ *
   * WEAPON — rigid-skinned to the right hand, zero extra draw calls
   * ================================================================ */
  if (L.weapon) {
    const h = rig.bind.handR;
    const wR = [[I('handR'), 1]];
    const P = (x, y, z) => V(h.x + x * S, h.y + y * S, h.z + z * S);
    const box = (c, half, round, tint, su = 8, sv = 5) => {
      B.setTint(tint);
      B.superBox({ centre: c, half: half.map((v) => v * S), round, segU: su, segV: sv, w: wR });
    };
    B.setSlot(SLOT.HARD);
    box(P(0, -0.052, -0.020), [0.018, 0.052, 0.024], 0.34, 0x24242a);         // pistol grip
    box(P(0, 0.052, 0.028), [0.023, 0.038, 0.112], 0.22, 0x3a3c40, 9, 5);     // receiver
    box(P(0, 0.055, 0.222), [0.025, 0.031, 0.108], 0.24, 0x2c2e31, 9, 5);     // handguard
    box(P(0, 0.058, 0.352), [0.011, 0.011, 0.058], 0.85, 0x1c1d1f, 6, 4);     // barrel
    box(P(0, 0.058, 0.424), [0.015, 0.015, 0.026], 0.40, 0x232426, 7, 4);     // muzzle brake
    box(P(0, -0.028, 0.086), [0.017, 0.076, 0.030], 0.28, 0x2a2c2e, 8, 5);    // magazine
    box(P(0, 0.055, -0.086), [0.017, 0.017, 0.052], 0.55, 0x212224, 6, 4);    // buffer tube
    box(P(0, 0.048, -0.168), [0.021, 0.042, 0.084], 0.30, 0x2e3033, 8, 5);    // stock
    box(P(0, 0.100, 0.096), [0.020, 0.030, 0.048], 0.30, 0x35373a, 8, 5);     // optic
    box(P(0, 0.128, 0.096), [0.017, 0.008, 0.040], 0.40, 0x15161a, 7, 4);     // optic glass hood
    box(P(0, 0.010, 0.256), [0.016, 0.034, 0.020], 0.40, 0x26282a, 7, 4);     // angled foregrip
    box(P(0, 0.088, 0.196), [0.010, 0.010, 0.086], 0.30, 0x2a2c2e, 6, 4);     // top rail

    // Anchors come from Rig.WEAPON_LOCAL so Body.js can wrap the fingers round exactly
    // the same numbers this weapon is built from.
    Object.assign(out, weaponAnchors(rig));
  }

  return out;
}
